import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getPowerBIReportDetail from "@tKgpco/Handlers/PowerBI/GetPowerBIReportDetail";
import getPowerBIReportNames from "@tKgpco/Handlers/PowerBI/GetPowerBIReportNames";
import { ReportDetailsResult } from "@tKgpco/Services/PowerBiService";
import isEmpty from "lodash/isEmpty";
import React, { Suspense, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const PowerBIComp = React.lazy(() => import("@tKgpco/Components/PowerBi/PowerBiComp"));

const enum fields {
    reportId = "reportId",
    filters = "filters",
    workspaceId = "workspaceId",
}

const mapStateToProps = (state: ApplicationState) => ({
    userName: state.context.session.userName,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isLoggedIN: state.context.session.isAuthenticated,
    featurePages: state.context.session.properties.featurePages,
    isSecurityRolesAssigned: state.context.session?.properties?.isSecurityRolesAssigned?.toLowerCase() === "true",
});

export interface Styles {
    container?: GridContainerProps;
    items?: GridItemProps;
    reportItem?: GridItemProps;
    filterItems?: GridItemProps;
    mainContainer?: InjectableCss;
    filterGridItems?: GridItemProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
}

const mainStyles: Styles = {
    container: {
        css: css`
            flex-direction: column;
            padding: 20px;
            border-radius: 5px;
            background-color: #f1f1f1;
        `,
    },
    items: {
        width: [12, 12, 2, 2, 2],
        css: css`
            button {
                width: 100%;
            }
        `,
    },
    reportItem: {
        width: [12, 12, 3, 3, 3],
    },
    filterItems: {
        width: [12, 12, 9, 9, 9],
    },
    mainContainer: {
        css: css`
            margin-top: 30px;
        `,
    },
    filterGridItems: {
        width: [12, 12, 4, 4, 4],
        css: css`
            padding: 0;
            padding-right: 15px;

            &.apply-filters-button {
                align-self: flex-end;
            }
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
};

const styles = mainStyles;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.reportId]: string;
        [fields.filters]: string;
        [fields.workspaceId]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapDispatchToProps = {
    getPowerBIReportNames,
    getPowerBIReportDetail,
};

const PowerBiIntegration: React.FC<Props> = ({
    fields,
    getPowerBIReportNames,
    getPowerBIReportDetail,
    userName,
    signInUrl,
    currentUserIsGuest,
    history,
    featurePages,
    isSecurityRolesAssigned,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [result, setResult] = useState<ReportDetailsResult | null>(null);
    const [reports, setReports] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedReport, setSelectedReport] = useState("");
    const { reportId, filters, workspaceId } = fields;
    const { isInShell } = useShellContext();
    const [editableFilters, setEditableFilters] = useState<any[]>([]);

    useEffect(() => {
        if (!(userName && !currentUserIsGuest)) {
            history.push(`${signInUrl}`);
        }
    }, [userName, currentUserIsGuest]);

    useEffect(() => {
        if (!(userName && !currentUserIsGuest)) {
            return;
        }

        getPowerBIReportNames({
            onSuccess(result) {
                setReports(result);
                // setSelectedReport(result[0]);
            },
            onError(message) {
                // toasterContext.addToast({
                //     body: message,
                //     messageType: "danger",
                // });
            },
        });
    }, [userName, currentUserIsGuest]);

    useEffect(() => {
        if (!selectedReport) {
            return;
        }
        setLoading(true);
        setResult(null);
        getPowerBIReportDetail({
            reportName: selectedReport,
            onSuccess(result) {
                setResult(result);
                setEditableFilters(result.filters || []);
                setLoading(false);
            },
            onError(message) {
                // setResult(null);
                // toasterContext.addToast({
                //     body: message,
                //     messageType: "danger",
                // });
                setLoading(false);
            },
        });
    }, [selectedReport]);

    if (featurePages?.includes("Embedded Reports")) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="no-access-message">
                    {translate(`You don’t have permission to view this page`)}
                </Typography>
            </StyledWrapper>
        );
    }

    if (!reports.length) {
        return (
            <StyledWrapper
                css={css`
                    display: flex;
                    justify-content: center;
                    margin-top: 30px;
                `}
            >
                <Typography variant="h2">{translate("No Reports Present")}</Typography>
            </StyledWrapper>
        );
    }

    const handleFilterChange = (index: number, value: string) => {
        setEditableFilters(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                values: [value.trim()],
            };
            return updated;
        });
    };

    const applyFilters = () => {
        setResult(prev =>
            prev
                ? {
                      ...prev,
                      filters: editableFilters,
                  }
                : prev,
        );
    };

    const handleChangeReport = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedReport(e.target.value);
    };

    return (
        <StyledWrapper {...styles.mainContainer}>
            {reports.length ? (
                <GridContainer {...styles.container}>
                    <GridItem {...styles.reportItem}>
                        <Select
                            value={selectedReport}
                            onChange={handleChangeReport}
                            label={translate("Available Reports")}
                            labelProps={{ size: "14px" }}
                        >
                            <option value="">{translate("Select Report")}</option>
                            {reports.map(reportName => (
                                <option key={reportName} value={reportName}>
                                    {reportName}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.filterItems}>
                        {editableFilters?.length
                            ? editableFilters.map((filter, index) => {
                                  const value =
                                      Array.isArray(filter.values) && filter.values.length ? filter.values[0] : "";

                                  return (
                                      <GridItem key={index} {...styles.filterGridItems}>
                                          <TextField
                                              label={filter?.target?.column}
                                              labelProps={{ size: "14px" }}
                                              disabled={!filter.customer_editable}
                                              value={value}
                                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                  handleFilterChange(index, e.target.value)
                                              }
                                          />
                                      </GridItem>
                                  );
                              })
                            : null}
                        {editableFilters?.length ? (
                            <GridItem {...styles.filterGridItems} className="apply-filters-button">
                                <Button onClick={applyFilters}>{translate("Refresh")}</Button>
                            </GridItem>
                        ) : null}
                    </GridItem>
                </GridContainer>
            ) : (
                <Typography variant="h2">{translate("No Reports Present")}</Typography>
            )}

            <Suspense fallback={<LoadingSpinner />}>
                <>
                    {result?.embedInfo.name ? (
                        <Typography variant="h1">{`${translate("Now Showing")}: ${selectedReport}`}</Typography>
                    ) : null}
                    {result && result?.token && (
                        <PowerBIComp
                            token={result?.token}
                            reportId={result?.embedInfo.id}
                            embedUrl={result?.embedInfo.embedUrl}
                            filters={
                                result?.filters?.length ? result?.filters?.filter(f => f.values.join(",").trim()) : null
                            }
                            workspaceId={result?.embedInfo.datasetWorkspaceId}
                            reportType={result?.embedInfo.reportType}
                        />
                    )}
                </>
            </Suspense>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(PowerBiIntegration)),
    definition: {
        group: "Common",
        displayName: "PowerBiIntegration",
        fieldDefinitions: [
            {
                name: fields.reportId,
                displayName: "Report Id",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.filters,
                displayName: "Filters(JSON)",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.workspaceId,
                displayName: "Workspace Id",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
